import { useMemo } from 'react';

import { usePostHog } from 'posthog-js/react';
import { User } from '~/contexts/UserAuthProvider';

// Types
type InteractionEvent = 'click' | 'focus' | 'blur' | 'submit' | 'scroll';
type ImpressionEvent = 'view';

// Exports
export const useAnalytics = () => {
  const client = usePostHog();

  return useMemo(
    () => ({
      /**
       * Track user interactions, such as button clicks, form submits, scrolls, etc. Requires an
       * `elementId` to be set.
       */
      trackInteraction: (
        eventType: InteractionEvent,
        elementId: string,
        props: Record<string, any>,
      ) => {
        client.capture(eventType, { ...props, elementId });
      },

      /**
       * Track user impressions, aka "did the user see a significant content change". This can be
       * anything from a modal or a drawer opening, to accordions expanding, to proceeding through
       * a multi-step workflow.
       *
       * ⚠️ This is not the same thing as a page view. Page views are determined by browser URL
       * ⚠️ changes, and are tracked automatically. Only use `trackImpression` when you don't
       * ⚠️ already have a page view you can use (e.g. modals, drawers, alerts).
       */
      trackImpression: (
        eventType: ImpressionEvent,
        elementId: string,
        props: Record<string, any>,
      ) => {
        client.capture(eventType, { ...props, elementId });
      },

      /** Set user properties upon login. Used to trace user sessions, among other things. */
      identify: (user: User) => {
        const userEmailDomain = user.email.split('@').pop();
        client.identify(user.id, {
          roleType: user.roleType,
          userId: user.id,
          userType: user.userType,
          userEmailDomain,
          userEmail:
            // Use redacted email, unless it's a Finley user
            userEmailDomain === 'finleycms.com'
              ? user.email
              : `***@${userEmailDomain}`,
          organizationId: user.organization.id,
          organizationName: user.organization.name,
          organizationSlug: user.organization.slug,
        });
      },

      /** Reset user properties upon logout. */
      reset: () => {
        client.reset();
      },

      client,
    }),
    [client],
  );
};
